import type { DatasetEvent } from "../../api/types";
import { formatClock, formatDateTime } from "../../lib/format";

interface EventTraceProps {
  events: DatasetEvent[];
}

/**
 * The trace of what happened to this client's data, in the console's words.
 *
 * This is how the product explains itself: every change appends one plain
 * sentence carrying its own numbers, and the interface shows them **verbatim
 * and in order** rather than paraphrasing. Nothing here folds, groups or
 * summarises — for most of this product's readers these sentences *are* the
 * product, and a line that was hidden is a line somebody will look for once
 * and never trust again.
 *
 * Args:
 *     events: The trace as served, oldest first.
 *
 * Returns:
 *     An ordered trace.
 */
export function EventTrace({ events }: EventTraceProps): JSX.Element {
  if (events.length === 0) {
    return <p className="timeline__empty">Nothing has happened yet.</p>;
  }

  return (
    <ol className="timeline">
      {events.map((event, index) => (
        <li
          className="timeline__item"
          data-kind={event.kind}
          data-last={index === events.length - 1 ? "true" : "false"}
          key={event.index}
        >
          <span className="timeline__marker" aria-hidden="true" />
          <time
            className="timeline__time mono"
            dateTime={event.at}
            title={formatDateTime(event.at)}
          >
            {formatClock(event.at)}
          </time>
          <span className="timeline__message">
            {event.version_number ? (
              <span className="timeline__version mono">
                v{event.version_number}
              </span>
            ) : null}
            {event.message}
          </span>
        </li>
      ))}
    </ol>
  );
}
